import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardHeader from './service_provider/DashboardHeader';
import StatsCards from './service_provider/StatsCards';
import ServiceRequestsPanel from './service_provider/ServiceRequestsPanel';
import ProposalsPanel from './service_provider/ProposalsPanel';
import CreateRequestModal from './service_provider/CreateRequestModal';
import ProposalDetailsModal from './service_provider/ProposalDetailsModal';
import EditProposalModal from './service_provider/EditProposalModal';

const ServiceProviderDashboard = () => {
  const { user } = useAuth();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState({
    activeRequests: 0,
    pendingProposals: 0,
    acceptedProposals: 0,
    totalEarnings: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateRequest, setShowCreateRequest] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [showProposalDetailsModal, setShowProposalDetailsModal] = useState(false);
  const [showEditProposalModal, setShowEditProposalModal] = useState(false);
  const [editProposalForm, setEditProposalForm] = useState({
    title: '',
    proposedAmount: '',
    estimatedDuration: '',
    description: '',
    workScope: [],
    materials: [],
    notes: ''
  });
  const [createRequestForm, setCreateRequestForm] = useState({
    title: '',
    description: '',
    property: '',
    budget: '',
    deadline: '',
    priority: 'Medium',
    serviceType: '',
    requirements: ''
  });

  useEffect(() => {
    // Simulate loading service provider data
    const mockRequests = [
      {
        id: 1,
        title: 'Commercial HVAC Installation',
        property: 'Downtown Office Complex',
        description: 'Install new HVAC system for 5000 sq ft office space',
        budget: 'R 15 000 - R 20 000',
        deadline: '2024-02-15',
        status: 'Open',
        priority: 'High',
        postedDate: '2024-01-10'
      },
      {
        id: 2,
        title: 'Plumbing System Upgrade',
        property: 'Residential Complex A',
        description: 'Upgrade plumbing system for 20-unit residential building',
        budget: 'R 8 000 - R 12 000',
        deadline: '2024-02-28',
        status: 'Open',
        priority: 'Medium',
        postedDate: '2024-01-12'
      },
      {
        id: 3,
        title: 'Electrical Panel Replacement',
        property: 'Retail Center B',
        description: 'Replace outdated electrical panels in retail center',
        budget: 'R 5 000 - R 7 500',
        deadline: '2024-01-30',
        status: 'Proposal Submitted',
        priority: 'High',
        postedDate: '2024-01-08'
      }
    ];

    const mockProposals = [
      {
        id: 1,
        requestId: 3,
        title: 'Electrical Panel Replacement',
        proposedAmount: 'R 6 200',
        status: 'Under Review',
        submittedDate: '2024-01-13',
        estimatedDuration: '5 days',
        description: 'Complete replacement of outdated electrical panels in retail center with modern, code-compliant units. Includes safety inspection and testing.',
        property: 'Retail Center B',
        serviceType: 'Electrical',
        priority: 'High',
        deadline: '2024-01-30',
        workScope: [
          'Remove existing electrical panels',
          'Install new code-compliant panels',
          'Update wiring connections',
          'Perform safety testing',
          'Provide certification documentation'
        ],
        materials: [
          '2x Main electrical panels (200A)',
          'Circuit breakers (various sizes)',
          'Electrical conduit and fittings',
          'Safety labels and documentation'
        ],
        clientContact: {
          name: 'Sarah Johnson',
          phone: '+27 11 456 7890',
          email: 'sarah.johnson@retailcenter.com',
          role: 'Facilities Manager'
        },
        notes: 'Client requires work to be completed during off-hours to minimize business disruption. All work must comply with local electrical codes.'
      }
    ];

    setTimeout(() => {
      setServiceRequests(mockRequests);
      setProposals(mockProposals);
      setStats({
        activeRequests: mockRequests.filter(r => r.status === 'Open').length,
        pendingProposals: mockProposals.filter(p => p.status === 'Under Review').length,
        acceptedProposals: mockProposals.filter(p => p.status === 'Accepted').length,
        totalEarnings: 45000 // Mock total earnings
      });
      setLoading(false);
    }, 1000);
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-600 bg-red-100';
      case 'Medium': return 'text-yellow-600 bg-yellow-100';
      case 'Low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Open': return 'text-blue-600 bg-blue-100';
      case 'Proposal Submitted': return 'text-orange-600 bg-orange-100';
      case 'Under Review': return 'text-yellow-600 bg-yellow-100';
      case 'Accepted': return 'text-green-600 bg-green-100';
      case 'Rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const submitProposal = (requestId) => {
    // Mock proposal submission
    setServiceRequests(prev => prev.map(request =>
      request.id === requestId ? { ...request, status: 'Proposal Submitted' } : request
    ));
  };

  const createServiceRequest = async () => {
    try {
      if (!createRequestForm.title.trim() || !createRequestForm.description.trim()) {
        alert('Title and description are required');
        return;
      }

      setLoading(true);

      // Create new service request
      const newRequest = {
        id: Date.now(), // Mock ID generation
        title: createRequestForm.title,
        property: createRequestForm.property,
        description: createRequestForm.description,
        budget: createRequestForm.budget,
        deadline: createRequestForm.deadline,
        status: 'Open',
        priority: createRequestForm.priority,
        serviceType: createRequestForm.serviceType,
        requirements: createRequestForm.requirements,
        postedDate: new Date().toISOString().split('T')[0],
        createdBy: user?.company || user?.name || 'Service Provider'
      };

      // Add to service requests list
      setServiceRequests(prev => [newRequest, ...prev]);

      // Update stats
      setStats(prev => ({
        ...prev,
        activeRequests: prev.activeRequests + 1
      }));

      // Reset form and close modal
      setCreateRequestForm({
        title: '',
        description: '',
        property: '',
        budget: '',
        deadline: '',
        priority: 'Medium',
        serviceType: '',
        requirements: ''
      });
      setShowCreateRequest(false);

      alert('Service request created successfully!');
    } catch (error) {
      console.error('Error creating service request:', error);
      alert('Failed to create service request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit proposal
  const handleEditProposal = (proposal) => {
    setEditProposalForm({
      title: proposal.title || '',
      proposedAmount: proposal.proposedAmount || '',
      estimatedDuration: proposal.estimatedDuration || '',
      description: proposal.description || '',
      workScope: proposal.workScope || [],
      materials: proposal.materials || [],
      notes: proposal.notes || ''
    });
    setShowEditProposalModal(true);
  };

  // Save edited proposal
  const saveEditedProposal = () => {
    if (!editProposalForm.title.trim() || !editProposalForm.proposedAmount.trim()) {
      alert('Title and proposed amount are required');
      return;
    }

    // Update the proposal in the proposals array
    setProposals(prev => prev.map(proposal =>
      proposal.id === selectedProposal.id
        ? { ...proposal, ...editProposalForm }
        : proposal
    ));

    // Update the selected proposal for the details modal
    setSelectedProposal(prev => ({ ...prev, ...editProposalForm }));

    // Close edit modal
    setShowEditProposalModal(false);
    alert('Proposal updated successfully!');
  };

  // Download proposal as PDF
  const downloadProposalPDF = async () => {
    try {
      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      await import('jspdf-autotable');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;

      // Header
      doc.setFontSize(20);
      doc.text('Proposal Details', pageWidth / 2, 30, { align: 'center' });

      // Proposal information
      let yPosition = 50;
      doc.setFontSize(12);

      const addSection = (title, content) => {
        doc.setFont(undefined, 'bold');
        doc.text(title, margin, yPosition);
        yPosition += 10;
        doc.setFont(undefined, 'normal');

        if (Array.isArray(content)) {
          content.forEach(item => {
            doc.text(`• ${item}`, margin + 10, yPosition);
            yPosition += 8;
          });
        } else {
          const lines = doc.splitTextToSize(content, pageWidth - 2 * margin);
          lines.forEach(line => {
            doc.text(line, margin, yPosition);
            yPosition += 8;
          });
        }
        yPosition += 5;
      };

      addSection('Project Title:', selectedProposal.title);
      addSection('Property:', selectedProposal.property);
      addSection('Proposed Amount:', selectedProposal.proposedAmount);
      addSection('Estimated Duration:', selectedProposal.estimatedDuration);
      addSection('Submission Date:', selectedProposal.submittedDate);
      addSection('Project Deadline:', selectedProposal.deadline);
      addSection('Status:', selectedProposal.status);
      addSection('Priority:', selectedProposal.priority);
      addSection('Service Type:', selectedProposal.serviceType);

      if (selectedProposal.description) {
        addSection('Project Description:', selectedProposal.description);
      }

      if (selectedProposal.workScope && selectedProposal.workScope.length > 0) {
        addSection('Work Scope:', selectedProposal.workScope);
      }

      if (selectedProposal.materials && selectedProposal.materials.length > 0) {
        addSection('Materials & Equipment:', selectedProposal.materials);
      }

      if (selectedProposal.clientContact) {
        addSection('Client Contact:', [
          `Name: ${selectedProposal.clientContact.name}`,
          `Role: ${selectedProposal.clientContact.role}`,
          `Phone: ${selectedProposal.clientContact.phone}`,
          `Email: ${selectedProposal.clientContact.email}`
        ]);
      }

      if (selectedProposal.notes) {
        addSection('Additional Notes:', selectedProposal.notes);
      }

      // Footer
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(10);
      doc.text(`Generated on ${new Date().toLocaleString()}`, margin, pageHeight - 20);
      doc.text(`${user?.company || user?.name || 'Service Provider'}`, pageWidth - margin, pageHeight - 20, { align: 'right' });

      // Save the PDF
      const filename = `proposal_${selectedProposal.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);

      alert('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <DashboardHeader
        user={user}
        onCreateRequest={() => setShowCreateRequest(true)}
      />

      <StatsCards stats={stats} />

      {/* Service Requests & Proposals panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <ServiceRequestsPanel
          serviceRequests={serviceRequests}
          getPriorityColor={getPriorityColor}
          onSubmitProposal={submitProposal}
        />

        <ProposalsPanel
          proposals={proposals}
          getStatusColor={getStatusColor}
          onViewDetails={(proposal) => {
            setSelectedProposal(proposal);
            setShowProposalDetailsModal(true);
          }}
        />
      </div>

      {showCreateRequest && (
        <CreateRequestModal
          createRequestForm={createRequestForm}
          setCreateRequestForm={setCreateRequestForm}
          loading={loading}
          onClose={() => setShowCreateRequest(false)}
          onSubmit={createServiceRequest}
        />
      )}

      {showProposalDetailsModal && selectedProposal && (
        <ProposalDetailsModal
          selectedProposal={selectedProposal}
          getStatusColor={getStatusColor}
          getPriorityColor={getPriorityColor}
          onClose={() => {
            setSelectedProposal(null);
            setShowProposalDetailsModal(false);
          }}
          onEdit={handleEditProposal}
          onDownloadPDF={downloadProposalPDF}
        />
      )}

      {showEditProposalModal && (
        <EditProposalModal
          editProposalForm={editProposalForm}
          setEditProposalForm={setEditProposalForm}
          onClose={() => setShowEditProposalModal(false)}
          onSave={saveEditedProposal}
        />
      )}
    </div>
  );
};

export default ServiceProviderDashboard;
